//
// Panel.tsx — the main recorder panel. Owns the state for an
// animated timeline render: the detected map, an initial camera
// view, a list of actions, total duration, fade envelope, current
// scrub position, and the render lifecycle.
//
// Sub-components (Timeline, ActionEditor) are presentational —
// they receive props and dispatch callbacks. The panel is the
// source of truth for everything.
//
// Render flow (handleRender):
//
//   1. User clicks Render. While we still have the user-gesture
//      token, call openResultChannel() — this window.open must
//      happen synchronously in the click handler or the browser's
//      popup blocker will kill it.
//   2. If the popup opened, snapshot the live camera so we can
//      restore it when we're done, and send start metadata.
//   3. Drive render() with the timeline + initialView. Forward
//      progress to both our progress bar and the popup. If the
//      popup closes mid-render, the onProgress callback throws and
//      aborts.
//   4. On success, post the MP4; on failure, post the error.
//   5. Either way, restore the saved camera.
//
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import type { LicenseInfo } from '@mve/protocol';
import { captureView, planRender, render as renderVideo, supportsWebCodecs } from '../capture';
import { consumeCredit } from '../license';
import {
  computeLayerOpacities,
  computeViewAtTime,
  createAction,
  trackOf,
  type Action,
  type LayerFade,
  type MapView,
  type PanAction,
  type PitchAction,
  type RotateAction,
  type Timeline,
  type Track,
  type ZoomAction,
} from '../timeline';
import { getInstances, onInstance } from '../detect';
import {
  createLayerFadeController,
  createLayerHideController,
  type LayerFadeController,
  type LayerHideController,
} from '../layerHide';
import { SCENARIOS, type Scenario } from '../scenarios';
import { openResultChannel, type ResultChannel } from '../transport';

//
// Default width in pixels for the first render triggered by the
// Render button. Kept deliberately low so the initial turnaround
// is fast: users get a rough preview quickly, then pick a higher
// preset in the receiver for the final output.
//
const PREVIEW_WIDTH = 640;
//
// Width above which a render counts as "hires" and consumes a
// credit. ≤ this width is the free preview tier and never costs
// anything, regardless of the user's license tier — so a paid
// user testing at 640 isn't billed each tweak. Match the free-
// tier ceiling on the server (FREE_TIER.maxWidth in license.ts).
//
const FREE_MAX_WIDTH = 720;
// Ceiling applied to any requested render width. Derived from the
// tier returned by /api/embed/license — free tier users can still
// click the Render button but the output is clamped to 720 wide.
function clampWidth(requested: number, license: LicenseInfo): number {
  return Math.min(requested, license.maxWidth);
}
import { ActionEditor } from './ActionEditor';
import { Timeline as TimelineComponent } from './Timeline';
import {
  buttonStyle,
  disabledButtonStyle,
  dropdownItemStyle,
  dropdownMenuStyle,
  headerStyle,
  labelSpanStyle,
  layerListStyle,
  layerRowStyle,
  layerTypeBadgeStyle,
  mapInfoStyle,
  panelBodyStyle,
  panelFooterStyle,
  panelRootStyle,
  progressBarStyle,
  progressWrapStyle,
  rowStyle,
  selectStyle,
  smallInputStyle,
  statusStyle,
  statusTone,
  tabBadgeStyle,
  tabBarStyle,
  tabButtonStyle,
  textareaStyle,
  titleStyle,
  viewRowStyle,
  type StatusTone,
} from './styles';

//
// Minimal shape we need from a Mapbox style layer — just id and
// type. The full LayerSpecification is sprawling and we only
// display these two.
//
interface LayerInfo {
  id: string;
  type: string;
}

interface Status {
  text: string;
  tone: StatusTone;
}

//
// The four categories the panel body is organised under. Header
// and footer stay pinned across tabs; only the body swaps.
//
type TabKey = 'animation' | 'effects' | 'overlay' | 'layers';

export interface PanelProps {
  onClose: () => void;
  //
  // Tier + resolution caps derived at boot from the API. Advisory
  // in the UI (we cap preview width and forward to the receiver so
  // its resolution picker hides presets > maxWidth); real
  // enforcement lives server-side at the credit-consume step.
  //
  license: LicenseInfo;
}

export function Panel({ onClose, license: initialLicense }: PanelProps): JSX.Element {
  //
  // License kept as panel-local state so we can decrement
  // creditsRemaining locally after each successful consume call.
  // Re-fetching from the server after every render would be
  // wasteful — the consume response already tells us the new
  // balance. The initial value comes from props (set once at boot
  // by main.ts → fetchLicense → mountLauncher).
  //
  const [license, setLicense] = useState<LicenseInfo>(initialLicense);
  //
  // Detected Mapbox instances. Re-read via onInstance when the
  // detector finds new ones (MutationObserver ticks, React fiber
  // walks on late-appearing containers).
  //
  const [mapInstances, setMapInstances] = useState(() => getInstances());
  //
  // The camera state at t=0. The user has to set this before
  // rendering so interpolation has a baseline for properties with
  // no covering action.
  //
  const [initialView, setInitialView] = useState<MapView | null>(null);
  const [actions, setActions] = useState<Action[]>([]);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [totalSeconds, setTotalSeconds] = useState<number>(10);
  //
  // Fade envelope, in seconds. Zero means the fade is disabled;
  // we translate that to `undefined` when building the Timeline
  // so the engine's alpha calc can skip cheaply.
  //
  const [fadeIn, setFadeIn] = useState<number>(0);
  const [fadeOut, setFadeOut] = useState<number>(0);
  //
  // Current preview time for scrubbing. Drives the playhead in
  // the Timeline component and, when changed, jumps the live map
  // to the evaluated view at that time.
  //
  const [scrubT, setScrubT] = useState<number>(0);
  const [rendering, setRendering] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressShown, setProgressShown] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  //
  // Overlay capture configuration, held as raw multi-line strings
  // so the user can edit freely (blank lines, half-typed selectors,
  // etc.) without us parsing on every keystroke. Parsed into arrays
  // only at render time and when loading a scenario.
  //
  const [includeSelectorsRaw, setIncludeSelectorsRaw] = useState<string>('');
  const [excludeSelectorsRaw, setExcludeSelectorsRaw] = useState<string>('');
  //
  // Mapbox style layers the user wants hidden during render, plus
  // the latest snapshot of layers present in the style and a
  // filter string for searching the list. Layer ids stored here
  // may include ids that aren't currently in the map (e.g. if
  // preset by a scenario targeting a different style) — those are
  // ignored at render time and the list UI only shows layers that
  // actually exist.
  //
  const [hiddenLayerIds, setHiddenLayerIds] = useState<string[]>([]);
  //
  // Per-layer opacity fades. Each entry drives one layer's paint
  // opacity between two times. Rendered as a list in the Effects
  // tab; live-previewed via the scrubber; applied per-frame during
  // render.
  //
  const [layerFades, setLayerFades] = useState<LayerFade[]>([]);
  const [availableLayers, setAvailableLayers] = useState<LayerInfo[]>([]);
  const [layerFilter, setLayerFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabKey>('animation');
  //
  // Two-click confirm for the destructive "clear all settings"
  // action. First click flips to true and the button re-renders
  // as "Confirm?"; second click within the timeout commits. A
  // runaway timer reverts the pending state after 3 seconds so
  // the button can't stay armed indefinitely.
  //
  const [clearPending, setClearPending] = useState(false);
  const clearTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (clearTimerRef.current !== null) window.clearTimeout(clearTimerRef.current);
  }, []);
  //
  // Cancellation flag for an ongoing render. Set by the Cancel
  // button in the render overlay; checked inside onProgress, which
  // throws to abort the render loop. Reset at the start of each
  // new render.
  //
  const cancelRequestedRef = useRef<boolean>(false);
  //
  // Live layer-visibility override kept in sync with hiddenLayerIds
  // so toggling a layer in the panel immediately reflects on the
  // host app's map. The controller attaches monkey-patches on the
  // map for the duration of the panel being open and is torn down
  // on unmount; see layerHide.ts.
  //
  const hideControllerRef = useRef<LayerHideController | null>(null);
  //
  // Live per-layer-opacity controller — mirrors layerFades evaluated
  // at the current scrub position so the user previews the fade
  // without rendering. Lifecycle parallel to hideControllerRef.
  //
  const fadeControllerRef = useRef<LayerFadeController | null>(null);

  useEffect(() => onInstance(() => setMapInstances(getInstances())), []);

  //
  // Keep the layer list in sync with whatever style the map is
  // currently using. Re-fetched on every 'styledata' event so if
  // the host app loads a new style or adds/removes layers at
  // runtime, the picker reflects it without the user clicking
  // refresh.
  //
  useEffect(() => {
    const map = mapInstances[0];
    if (!map) {
      setAvailableLayers([]);
      return undefined;
    }
    const update = (): void => {
      try {
        const style = map.getStyle();
        const layers = style?.layers ?? [];
        setAvailableLayers(layers.map((l) => ({ id: l.id, type: l.type })));
      } catch {
        setAvailableLayers([]);
      }
    };
    update();
    map.on('styledata', update);
    return () => {
      try { map.off('styledata', update); } catch { /* map gone */ }
    };
  }, [mapInstances]);

  useEffect(() => {
    if (!supportsWebCodecs()) {
      setStatus({
        text: 'WebCodecs not available. Use a recent Chrome/Edge.',
        tone: 'error',
      });
    }
  }, []);

  //
  // Keep a live LayerHideController attached to the selected map,
  // with its hidden set mirroring hiddenLayerIds. Toggling a layer
  // in the panel immediately shows/hides it on the host map; the
  // controller survives across renders and is torn down only when
  // the panel unmounts.
  //
  useEffect(() => {
    const map = mapInstances[0];
    if (!map) return;
    if (!hideControllerRef.current) {
      hideControllerRef.current = createLayerHideController(map);
    }
    hideControllerRef.current.setHidden(hiddenLayerIds);
  }, [mapInstances, hiddenLayerIds]);

  //
  // Unmount-only teardown: restores the host app's original layer
  // visibility and removes the monkey-patches. Not tied to the map
  // or IDs effect above so visibility state survives re-renders.
  //
  useEffect(() => () => {
    hideControllerRef.current?.teardown();
    hideControllerRef.current = null;
  }, []);

  //
  // Keep a LayerFadeController attached to the selected map and
  // push the opacities-at-scrubT through it whenever layerFades
  // or the scrub position changes. The result: as the user moves
  // the scrubber, faded layers smoothly transition in/out on the
  // host map just as they will in the render.
  //
  useEffect(() => {
    const map = mapInstances[0];
    if (!map) return;
    if (!fadeControllerRef.current) {
      fadeControllerRef.current = createLayerFadeController(map);
    }
    fadeControllerRef.current.setOpacityFactors(
      computeLayerOpacities(layerFades, scrubT),
    );
  }, [mapInstances, layerFades, scrubT]);

  useEffect(() => () => {
    fadeControllerRef.current?.teardown();
    fadeControllerRef.current = null;
  }, []);

  const selectedMap = mapInstances[0] ?? null;
  const webCodecsOk = supportsWebCodecs();
  const selectedAction = useMemo(
    () => actions.find((a) => a.id === selectedActionId) ?? null,
    [actions, selectedActionId],
  );
  //
  // The Timeline passed to both the visual component and the
  // render engine. `fadeIn === 0` collapses to undefined so the
  // engine can skip the alpha path entirely.
  //
  const timeline: Timeline = useMemo(
    () => ({
      actions,
      totalSeconds,
      ...(fadeIn > 0 ? { fadeIn } : {}),
      ...(fadeOut > 0 ? { fadeOut } : {}),
    }),
    [actions, totalSeconds, fadeIn, fadeOut],
  );

  const canRender = !rendering && !!selectedMap && !!initialView && webCodecsOk;

  const handleSetInitialView = (): void => {
    if (!selectedMap) return;
    setInitialView(captureView(selectedMap));
  };

  //
  // Create and insert a new action of the requested kind. Picks
  // sensible defaults for each type (rotate → 360° delta over 4 s,
  // others → 2 s using the live map state as the target). Places
  // the action at the user's scrub position if there's room, else
  // after the last existing action on that track.
  //
  const handleAddAction = (kind: 'pan' | 'zoom' | 'pitch' | 'rotate'): void => {
    if (!selectedMap) return;
    const live = captureView(selectedMap);
    const preferredStart = Math.max(0, Math.min(scrubT, totalSeconds - 0.2));
    const duration = kind === 'rotate' ? 4 : 2;
    const slot = findFreeSlot(actions, kind, preferredStart, duration, totalSeconds);
    if (!slot) {
      setStatus({
        text: 'No room left on that track. Shrink or delete an existing action first.',
        tone: 'error',
      });
      return;
    }
    const common = { start: slot.start, end: slot.end, easing: 'ease-in-out' as const };
    let action: Action;
    switch (kind) {
      case 'pan':
        action = createAction<PanAction>({
          ...common,
          type: 'pan',
          target: { lng: live.center.lng, lat: live.center.lat },
        });
        break;
      case 'zoom':
        action = createAction<ZoomAction>({
          ...common,
          type: 'zoom',
          target: live.zoom,
        });
        break;
      case 'pitch':
        action = createAction<PitchAction>({
          ...common,
          type: 'pitch',
          target: live.pitch > 0 ? live.pitch : 60,
        });
        break;
      case 'rotate':
        action = createAction<RotateAction>({
          ...common,
          type: 'rotate',
          mode: 'delta',
          deltaDegrees: 360,
        });
        break;
    }
    setActions((prev) => [...prev, action]);
    setSelectedActionId(action.id);
    setStatus(null);
  };

  const handleUpdateAction = (updated: Action): void => {
    setActions((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };

  const handleDeleteAction = (): void => {
    if (!selectedAction) return;
    setActions((prev) => prev.filter((a) => a.id !== selectedAction.id));
    setSelectedActionId(null);
  };

  //
  // Snapshot the live map view and write the relevant property
  // into the currently selected action as its new target.
  //
  const handleCaptureTarget = (): void => {
    if (!selectedMap || !selectedAction) return;
    const view = captureView(selectedMap);
    switch (selectedAction.type) {
      case 'pan':
        handleUpdateAction({ ...selectedAction, target: { lng: view.center.lng, lat: view.center.lat } });
        break;
      case 'zoom':
        handleUpdateAction({ ...selectedAction, target: view.zoom });
        break;
      case 'pitch':
        handleUpdateAction({ ...selectedAction, target: view.pitch });
        break;
      case 'rotate':
        if (selectedAction.mode === 'absolute') {
          handleUpdateAction({ ...selectedAction, target: view.bearing });
        }
        break;
    }
  };

  //
  // Scrubber handler: updates the preview time and, if we have a
  // map + initial view, jumps the map to the evaluated camera
  // state. A "preview" in the sense that the user can see the
  // animation at arbitrary times without going through a render.
  //
  const handleScrub = (t: number): void => {
    setScrubT(t);
    if (!selectedMap || !initialView) return;
    const view = computeViewAtTime(timeline, initialView, t);
    try {
      selectedMap.jumpTo({
        center: [view.center.lng, view.center.lat],
        zoom: view.zoom,
        bearing: view.bearing,
        pitch: view.pitch,
      });
    } catch { /* map went away mid-scrub */ }
  };

  //
  // Replace the current state with a preset scenario. Fresh ids
  // are minted for the actions so that if the user loads the same
  // scenario twice, the second load doesn't share action ids with
  // the stale UI state. Also jumps the live map to the scenario's
  // initial view so the user sees the starting point immediately.
  //
  const handleLoadScenario = (scenario: Scenario): void => {
    setInitialView(scenario.initialView);
    setActions(scenario.actions.map((a) => ({ ...a, id: crypto.randomUUID() }) as Action));
    setSelectedActionId(null);
    setTotalSeconds(scenario.totalSeconds);
    setFadeIn(scenario.fadeIn ?? 0);
    setFadeOut(scenario.fadeOut ?? 0);
    setScrubT(0);
    setStatus(null);
    const cfg = scenario.overlayConfig ?? { include: [], exclude: [] };
    setIncludeSelectorsRaw(cfg.include.join('\n'));
    setExcludeSelectorsRaw(cfg.exclude.join('\n'));
    setHiddenLayerIds(scenario.hiddenLayerIds ?? []);
    setLayerFades(
      (scenario.layerFades ?? []).map((f) => ({ ...f, id: crypto.randomUUID() })),
    );
    if (selectedMap) {
      try {
        selectedMap.jumpTo({
          center: [scenario.initialView.center.lng, scenario.initialView.center.lat],
          zoom: scenario.initialView.zoom,
          bearing: scenario.initialView.bearing,
          pitch: scenario.initialView.pitch,
        });
      } catch { /* noop */ }
    }
  };

  //
  // The result channel is long-lived: we keep it alive across
  // multiple renders so the receiver popup can ask for re-renders
  // at different resolutions without reopening. The ref is also
  // read inside onRequestRender below, which is captured into the
  // channel's handler when we first open it — once the channel
  // exists, future renders reuse it.
  //
  const channelRef = useRef<ResultChannel | null>(null);

  //
  // Latest handleRenderAt kept in a ref so the channel's
  // onRequestRender callback always invokes the current version
  // (with fresh closure state over timeline, initialView, etc.),
  // even though the channel itself was created many renders ago.
  //
  const latestRenderAtRef = useRef<(width: number) => Promise<void>>(
    async () => { /* replaced on first render */ },
  );

  const handleRenderAt = async (requestedWidth: number): Promise<void> => {
    if (!selectedMap || !initialView) return;
    // If a render is already running, ignore the request. The
    // receiver UI disables its buttons while rendering so this
    // shouldn't normally trigger in practice — defensive guard.
    if (rendering) return;

    // Free-tier visitors can still click Render at any width; we
    // silently clamp to the licensed maximum. The receiver's
    // resolution picker already hides presets above license.maxWidth,
    // so this is a belt-and-braces check for programmatic callers.
    const targetWidth = clampWidth(requestedWidth, license);

    // Reuse the existing result channel if we still have one and
    // the popup is alive. Otherwise open a fresh channel and wire
    // the re-render handler that lets the receiver request new
    // resolutions.
    let channel = channelRef.current;
    if (!channel || channel.isClosed()) {
      channel = openResultChannel({
        onRequestRender: (width) => {
          void latestRenderAtRef.current(width);
        },
      });
      if (!channel) {
        setStatus({
          text: 'Popup was blocked. Allow popups for this site, then click Render again.',
          tone: 'error',
        });
        channelRef.current = null;
        return;
      }
      channelRef.current = channel;
    }

    cancelRequestedRef.current = false;
    setRendering(true);
    setProgressShown(true);
    setProgress(0);
    setStatus({ text: `Rendering at ${targetWidth}px…`, tone: 'info' });

    const savedView = captureView(selectedMap);
    const baseMeta = planRender(selectedMap, timeline, 30, targetWidth);

    // Hires renders consume a credit; preview renders (≤ 720 wide)
    // are free for everyone. The consume call happens BEFORE we
    // post render-start to the receiver so the start metadata
    // reflects the post-decrement credit balance — otherwise the
    // receiver would display the stale pre-render count for the
    // entire render.
    let effectiveLicense = license;
    if (targetWidth > FREE_MAX_WIDTH) {
      const consumed = await consumeCredit({
        width: baseMeta.width,
        height: baseMeta.height,
        fps: baseMeta.fps,
        durationSeconds: baseMeta.durationSeconds,
        codec: 'vp09',
      });
      if (!consumed.ok) {
        const message = consumed.reason === 'insufficient_credits'
          ? 'Out of credits — buy more in your dashboard to render at this resolution.'
          : `Credit reservation failed (${consumed.reason ?? 'unknown'}).`;
        // No render-start has fired yet; sendError still works
        // because the receiver accepts errors in any phase.
        channel.sendError(message);
        setStatus({ text: message, tone: 'error' });
        setRendering(false);
        return;
      }
      if (typeof consumed.creditsRemaining === 'number') {
        effectiveLicense = { ...license, creditsRemaining: consumed.creditsRemaining };
        setLicense(effectiveLicense);
      }
    }

    // Attach the (now up-to-date) license to the render-start
    // metadata so the receiver's resolution picker shows the
    // correct credit balance.
    const startMeta = { ...baseMeta, license: effectiveLicense };
    channel.sendStart(startMeta);

    try {
      const result = await renderVideo(selectedMap, {
        initialView,
        timeline,
        fps: 30,
        overlayConfig: {
          include: parseSelectors(includeSelectorsRaw),
          exclude: parseSelectors(excludeSelectorsRaw),
        },
        hiddenLayerIds,
        layerFades,
        targetWidth,
        watermark: license.watermark,
        onProgress: (f) => {
          // Cancellation: user clicked the Cancel button, or closed
          // the receiver popup. Throwing here aborts the render
          // loop inside capture.ts.
          if (cancelRequestedRef.current) throw new Error('Render cancelled.');
          if (!channel || channel.isClosed()) throw new Error('Result window was closed.');
          setProgress(f);
          channel.sendProgress(f);
        },
      });
      channel.sendResult(result.blob, result.meta);
      setStatus({ text: 'Done — see the result window.', tone: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Render failed.';
      console.error('[mve] render failed', err);
      channel.sendError(message);
      setStatus({ text: message, tone: 'error' });
      // If the popup closed mid-render, drop the channel so the
      // next Render click opens a fresh one.
      if (channel.isClosed()) {
        channel.dispose();
        channelRef.current = null;
      }
    } finally {
      // NOTE: we deliberately do NOT dispose the channel here —
      // the receiver may ask for another render at a different
      // resolution. The channel's dispose happens when the user
      // closes the popup (detected via isClosed() above) or the
      // panel unmounts.
      try {
        selectedMap.jumpTo({
          center: [savedView.center.lng, savedView.center.lat],
          zoom: savedView.zoom,
          bearing: savedView.bearing,
          pitch: savedView.pitch,
        });
      } catch { /* noop */ }
      // The render loop drove the fade controller through every
      // frame, so its last state is "at t = totalSeconds". Re-apply
      // the controller at the live scrub position so the host map
      // doesn't stay stuck at the end-of-render opacities.
      fadeControllerRef.current?.setOpacityFactors(
        computeLayerOpacities(layerFades, scrubT),
      );
      setRendering(false);
    }
  };

  // Keep the ref pointing at the latest closure so channel-captured
  // callbacks always see current state.
  latestRenderAtRef.current = handleRenderAt;

  //
  // The Render button kicks off at the preview width. Users
  // upgrade resolution by picking presets in the receiver, which
  // round-trips back through onRequestRender above.
  //
  const handleRender = (): Promise<void> => handleRenderAt(PREVIEW_WIDTH);

  //
  // Cancel an ongoing render. Sets the ref; the next onProgress
  // tick throws and unwinds through capture.ts's catch/finally.
  // Safe to call before the next tick — the flag is just latched.
  //
  const handleCancelRender = (): void => {
    cancelRequestedRef.current = true;
    setStatus({ text: 'Cancelling…', tone: 'info' });
  };

  //
  // Clear every configurable setting back to defaults. First click
  // arms the button (sets clearPending); second click within the
  // 3 s timeout actually commits. activeTab is deliberately left
  // alone so the user's current view doesn't jump.
  //
  const handleClearClick = (): void => {
    if (clearPending) {
      if (clearTimerRef.current !== null) {
        window.clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      setClearPending(false);
      setInitialView(null);
      setActions([]);
      setSelectedActionId(null);
      setTotalSeconds(10);
      setScrubT(0);
      setFadeIn(0);
      setFadeOut(0);
      setIncludeSelectorsRaw('');
      setExcludeSelectorsRaw('');
      setHiddenLayerIds([]);
      setLayerFades([]);
      setLayerFilter('');
      setStatus(null);
      return;
    }
    setClearPending(true);
    clearTimerRef.current = window.setTimeout(() => {
      setClearPending(false);
      clearTimerRef.current = null;
    }, 3000);
  };

  //
  // Badge counts surfaced next to the tab labels so the user can
  // tell at a glance which tabs hold non-default state. For
  // Overlay capture we sum include + exclude selector counts; for
  // Mapbox layers it's just the number of layers marked hidden.
  // Zero collapses to undefined and the badge isn't rendered.
  //
  const overlayBadgeCount =
    parseSelectors(includeSelectorsRaw).length + parseSelectors(excludeSelectorsRaw).length;
  const layersBadgeCount = hiddenLayerIds.length;
  const effectsBadgeCount =
    (fadeIn > 0 ? 1 : 0) + (fadeOut > 0 ? 1 : 0) + layerFades.length;

  // While a render is in progress, replace the entire panel with
  // a small floating bar that just shows progress + cancel. The
  // panel's controls are all disabled mid-render anyway, so the
  // 720 px chrome was wasted screen real estate; this collapses it
  // to a strip the user can keep an eye on while doing other things.
  // The Panel component stays mounted (state preserved), it just
  // renders a different tree.
  if (rendering) {
    const pct = Math.round(progress * 100);
    return (
      <div
        style={{
          position: 'fixed',
          top: '12px',
          right: '12px',
          width: '320px',
          zIndex: 2147483647,
          background: '#0b1020',
          color: '#f3f4f6',
          border: '1px solid #374151',
          borderRadius: '8px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '13px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          padding: '12px 14px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
          <div
            style={{ fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={status?.text ?? 'Rendering…'}
          >
            {status?.text ?? 'Rendering…'}
          </div>
          <div style={{ fontVariantNumeric: 'tabular-nums', color: '#9ca3af' }}>{pct}%</div>
        </div>
        <div style={{ ...progressWrapStyle, marginTop: 0 }}>
          <div style={{ ...progressBarStyle, width: `${pct}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{
              ...buttonStyle({ danger: true }),
              width: 'auto',
              padding: '6px 14px',
            }}
            onClick={handleCancelRender}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={panelRootStyle}>
      <div style={headerStyle}>
        <div style={titleStyle}>Mapbox Video Export</div>
        <button style={buttonStyle({ subtle: true })} onClick={onClose}>✕</button>
      </div>

      <div style={tabBarStyle}>
        <TabButton active={activeTab === 'animation'} onClick={() => setActiveTab('animation')}>
          Animation
        </TabButton>
        <TabButton
          active={activeTab === 'effects'}
          badge={effectsBadgeCount > 0 ? effectsBadgeCount : undefined}
          onClick={() => setActiveTab('effects')}
        >
          Effects
        </TabButton>
        <TabButton
          active={activeTab === 'overlay'}
          badge={overlayBadgeCount > 0 ? overlayBadgeCount : undefined}
          onClick={() => setActiveTab('overlay')}
        >
          Overlay capture
        </TabButton>
        <TabButton
          active={activeTab === 'layers'}
          badge={layersBadgeCount > 0 ? layersBadgeCount : undefined}
          onClick={() => setActiveTab('layers')}
        >
          Mapbox layers
        </TabButton>
      </div>

      <div style={panelBodyStyle} className="mve-scroll">
      {activeTab === 'animation' && (
        <>
          <div style={mapInfoStyle}>{mapInfoText(mapInstances.length)}</div>

          <div style={{ ...rowStyle, marginTop: '4px' }}>
            <button
              style={{ ...buttonStyle({ compact: true }), ...(!selectedMap || rendering ? disabledButtonStyle : {}) }}
              onClick={handleSetInitialView}
              disabled={!selectedMap || rendering}
            >
              Set initial view
            </button>
            <span style={{ ...viewRowStyle, margin: 0, flex: 1 }}>
              {initialView
                ? `${initialView.center.lat.toFixed(4)}, ${initialView.center.lng.toFixed(4)} · z${initialView.zoom.toFixed(2)} · b${initialView.bearing.toFixed(0)}° · p${initialView.pitch.toFixed(0)}°`
                : '(not set)'}
            </span>
          </div>

          <TimelineComponent
            timeline={timeline}
            totalSeconds={totalSeconds}
            scrubT={scrubT}
            selectedActionId={selectedActionId}
            onScrub={handleScrub}
            onSelect={setSelectedActionId}
            onActionChange={handleUpdateAction}
          />

          <div style={rowStyle}>
            <span style={labelSpanStyle}>Add:</span>
            {(['pan', 'zoom', 'pitch', 'rotate'] as const).map((kind) => {
              const disabled = !selectedMap || rendering;
              return (
                <button
                  key={kind}
                  style={{ ...buttonStyle({ compact: true }), ...(disabled ? disabledButtonStyle : {}) }}
                  onClick={() => handleAddAction(kind)}
                  disabled={disabled}
                >
                  + {kind.charAt(0).toUpperCase() + kind.slice(1)}
                </button>
              );
            })}
            <div style={{ flex: 1 }} />
            <span style={labelSpanStyle}>Total</span>
            <NumberInput
              value={totalSeconds}
              step={0.5}
              min={1}
              max={120}
              disabled={rendering}
              onChange={setTotalSeconds}
            />
            <span style={labelSpanStyle}>s</span>
          </div>

          {selectedAction && (
            <ActionEditor
              action={selectedAction}
              onUpdate={handleUpdateAction}
              onDelete={handleDeleteAction}
              onCaptureTarget={handleCaptureTarget}
            />
          )}
        </>
      )}

      {activeTab === 'effects' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ ...rowStyle, marginTop: 0 }}>
            <span style={labelSpanStyle}>Fade in</span>
            <NumberInput value={fadeIn} step={0.25} min={0} max={5} onChange={setFadeIn} disabled={rendering} />
            <span style={labelSpanStyle}>s</span>
            <div style={{ width: '12px' }} />
            <span style={labelSpanStyle}>Fade out</span>
            <NumberInput value={fadeOut} step={0.25} min={0} max={5} onChange={setFadeOut} disabled={rendering} />
            <span style={labelSpanStyle}>s</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ ...labelSpanStyle, fontWeight: '600' }}>Layer fades</div>
            {layerFades.length === 0 ? (
              <div style={labelSpanStyle}>
                Fade a specific Mapbox layer in or out across a time range.
              </div>
            ) : (
              layerFades.map((fade) => (
                <LayerFadeRow
                  key={fade.id}
                  fade={fade}
                  layers={availableLayers}
                  totalSeconds={totalSeconds}
                  disabled={rendering}
                  onUpdate={(next) =>
                    setLayerFades((prev) => prev.map((f) => (f.id === fade.id ? next : f)))
                  }
                  onDelete={() =>
                    setLayerFades((prev) => prev.filter((f) => f.id !== fade.id))
                  }
                />
              ))
            )}
            <div>
              <button
                style={{
                  ...buttonStyle({ compact: true }),
                  ...(rendering || availableLayers.length === 0 ? disabledButtonStyle : {}),
                }}
                disabled={rendering || availableLayers.length === 0}
                onClick={() => {
                  const firstLayer = availableLayers[0];
                  if (!firstLayer) return;
                  setLayerFades((prev) => [
                    ...prev,
                    {
                      id: crypto.randomUUID(),
                      layerId: firstLayer.id,
                      direction: 'in',
                      startT: 0,
                      endT: Math.min(2, Math.max(0.5, totalSeconds)),
                    },
                  ]);
                }}
              >
                + Add layer fade
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'overlay' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <div style={{ ...labelSpanStyle, marginBottom: '2px' }}>
              Include selectors (one per line). Empty = map only; no overlays captured.
            </div>
            <textarea
              value={includeSelectorsRaw}
              onInput={(e) =>
                setIncludeSelectorsRaw((e.currentTarget as HTMLTextAreaElement).value)
              }
              style={textareaStyle}
              rows={5}
              placeholder=".mapboxgl-marker&#10;.selected-farm-panel"
              disabled={rendering}
              spellcheck={false}
            />
          </div>
          <div>
            <div style={{ ...labelSpanStyle, marginBottom: '2px' }}>
              Exclude selectors (one per line). Stripped even when inside an included element.
            </div>
            <textarea
              value={excludeSelectorsRaw}
              onInput={(e) =>
                setExcludeSelectorsRaw((e.currentTarget as HTMLTextAreaElement).value)
              }
              style={textareaStyle}
              rows={5}
              placeholder=".mapboxgl-ctrl&#10;.farm-name"
              disabled={rendering}
              spellcheck={false}
            />
          </div>
        </div>
      )}

      {activeTab === 'layers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ ...rowStyle, marginTop: 0 }}>
            <input
              type="text"
              value={layerFilter}
              onInput={(e) => setLayerFilter((e.currentTarget as HTMLInputElement).value)}
              placeholder="Filter by layer id…"
              disabled={rendering}
              style={{
                ...smallInputStyle,
                width: 'auto',
                flex: '1',
                textAlign: 'left',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            />
            {hiddenLayerIds.length > 0 && (
              <button
                style={{
                  ...buttonStyle({ compact: true }),
                  ...(rendering ? disabledButtonStyle : {}),
                }}
                onClick={() => setHiddenLayerIds([])}
                disabled={rendering}
              >
                Clear ({hiddenLayerIds.length})
              </button>
            )}
          </div>
          {availableLayers.length === 0 ? (
            <div style={labelSpanStyle}>No map detected, or style has no layers yet.</div>
          ) : (
            <div style={{ ...layerListStyle, maxHeight: 'none' }}>
              {filterLayers(availableLayers, layerFilter).map((layer) => {
                const checked = hiddenLayerIds.includes(layer.id);
                const toggle = (): void => {
                  setHiddenLayerIds((prev) =>
                    checked ? prev.filter((x) => x !== layer.id) : [...prev, layer.id],
                  );
                };
                return (
                  <label key={layer.id} style={layerRowStyle}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={toggle}
                      disabled={rendering}
                    />
                    <span style={{ flex: '1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {layer.id}
                    </span>
                    <span style={layerTypeBadgeStyle}>{layer.type}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
      </div>

      <div style={panelFooterStyle}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
          <ScenarioDropdown
            scenarios={SCENARIOS}
            disabled={rendering}
            onSelect={handleLoadScenario}
          />
          <button
            style={{
              ...buttonStyle({ primary: true }),
              flex: 1,
              ...(!canRender ? disabledButtonStyle : {}),
            }}
            onClick={() => void handleRender()}
            disabled={!canRender}
          >
            Render
          </button>
          <button
            style={{
              ...buttonStyle({ danger: true }),
              width: 'auto',
              padding: '8px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...(rendering ? disabledButtonStyle : {}),
            }}
            onClick={handleClearClick}
            disabled={rendering}
            title={clearPending ? 'Click again to confirm' : 'Clear all settings'}
          >
            {clearPending ? 'Confirm?' : <TrashIcon />}
          </button>
        </div>

        {progressShown && (
          <div style={progressWrapStyle}>
            <div style={{ ...progressBarStyle, width: `${Math.round(progress * 100)}%` }} />
          </div>
        )}

        <div style={{ ...statusStyle, ...(status ? { color: statusTone[status.tone] } : {}) }}>
          {status?.text ?? ''}
        </div>
      </div>
    </div>
  );
}

//
// Parse a multi-line CSS-selector blob into a clean string array.
// Trims each line and drops empties so trailing newlines or blank
// lines between selectors don't create invalid entries.
//
function parseSelectors(raw: string): string[] {
  return raw.split('\n').map((s) => s.trim()).filter((s) => s.length > 0);
}

//
// Case-insensitive substring filter for the layer picker. Empty
// filter returns the list unchanged.
//
function filterLayers(layers: LayerInfo[], filter: string): LayerInfo[] {
  const needle = filter.trim().toLowerCase();
  if (!needle) return layers;
  return layers.filter((l) => l.id.toLowerCase().includes(needle));
}

//
// Summary text for the detection status line.
//
function mapInfoText(count: number): string {
  if (count === 0) return 'No Mapbox map detected.';
  if (count === 1) return '1 map detected.';
  return `${count} maps detected (using the first).`;
}

//
// Return a { start, end } that fits `duration` seconds without
// overlapping any existing action on the same track, or null if
// there's no room. Strategy: try the user's preferred start; if
// siblings are in the way, slide past the last action's end. If
// the slot still doesn't fit inside totalSeconds, give up.
//
function findFreeSlot(
  existing: Action[],
  kind: 'pan' | 'zoom' | 'pitch' | 'rotate',
  preferredStart: number,
  duration: number,
  totalSeconds: number,
): { start: number; end: number } | null {
  const track: Track =
    kind === 'pan' ? 'center' :
    kind === 'zoom' ? 'zoom' :
    kind === 'pitch' ? 'pitch' : 'bearing';
  const onTrack = existing.filter((a) => trackOf(a) === track);
  const lastEnd = onTrack.reduce((m, a) => Math.max(m, a.end), 0);
  const start = Math.max(preferredStart, lastEnd);
  const end = Math.min(totalSeconds, start + duration);
  if (end - start < 0.2) return null;
  return { start, end };
}

//
// Compact dropdown for loading preset scenarios. Lives in the
// footer next to the Render button. The menu opens upward via
// `bottom: 100%` because the trigger sits at the bottom of the
// panel. Closes on item click, Escape, or pointer-down anywhere
// outside the dropdown's own DOM subtree.
//
function ScenarioDropdown(props: {
  scenarios: readonly Scenario[];
  disabled: boolean;
  onSelect: (scenario: Scenario) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent): void => {
      const root = containerRef.current;
      if (root && e.target instanceof Node && !root.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        style={{
          ...buttonStyle({ compact: false }),
          width: 'auto',
          padding: '8px 12px',
          ...(props.disabled ? disabledButtonStyle : {}),
        }}
        onClick={() => setOpen((o) => !o)}
        disabled={props.disabled}
      >
        Load demo ▾
      </button>
      {open && (
        <div style={dropdownMenuStyle} className="mve-scroll">
          {props.scenarios.map((scenario) => (
            <button
              key={scenario.name}
              style={dropdownItemStyle}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#1f2937')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
              onFocus={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#1f2937')}
              onBlur={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
              onClick={() => {
                props.onSelect(scenario);
                setOpen(false);
              }}
              title={scenario.description}
            >
              {scenario.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

//
// One row of the layer-fade editor in the Effects tab. Picks a
// Mapbox style layer, a direction (in/out), and the time window.
// The layer select includes any id currently configured in this
// fade even when it's not in the available list (e.g. because the
// style changed after the fade was added), so the user can still
// see and edit stale configuration instead of it vanishing.
//
function LayerFadeRow(props: {
  fade: LayerFade;
  layers: { id: string; type: string }[];
  totalSeconds: number;
  disabled: boolean;
  onUpdate: (next: LayerFade) => void;
  onDelete: () => void;
}): JSX.Element {
  const { fade, layers, totalSeconds, disabled, onUpdate, onDelete } = props;
  const hasCurrent = layers.some((l) => l.id === fade.layerId);
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 8px',
        border: '1px solid #1f2937',
        borderRadius: '6px',
        background: '#0b1020',
      }}
    >
      <select
        value={fade.layerId}
        disabled={disabled}
        onChange={(e) =>
          onUpdate({ ...fade, layerId: (e.currentTarget as HTMLSelectElement).value })
        }
        style={{
          ...selectStyle,
          flex: '1 1 160px',
          minWidth: '120px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}
      >
        {!hasCurrent && fade.layerId && (
          <option value={fade.layerId}>{fade.layerId} (not in style)</option>
        )}
        {layers.map((l) => (
          <option key={l.id} value={l.id}>{l.id}</option>
        ))}
      </select>
      <select
        value={fade.direction}
        disabled={disabled}
        onChange={(e) =>
          onUpdate({
            ...fade,
            direction: (e.currentTarget as HTMLSelectElement).value as 'in' | 'out',
          })
        }
        style={selectStyle}
      >
        <option value="in">Fade in</option>
        <option value="out">Fade out</option>
      </select>
      <NumberInput
        value={fade.startT}
        step={0.25}
        min={0}
        max={totalSeconds}
        disabled={disabled}
        onChange={(v) => onUpdate({ ...fade, startT: v })}
      />
      <span style={labelSpanStyle}>→</span>
      <NumberInput
        value={fade.endT}
        step={0.25}
        min={0}
        max={totalSeconds}
        disabled={disabled}
        onChange={(v) => onUpdate({ ...fade, endT: v })}
      />
      <span style={labelSpanStyle}>s</span>
      <button
        style={{
          ...buttonStyle({ subtle: true }),
          color: '#f87171',
          fontWeight: '700',
        }}
        disabled={disabled}
        onClick={onDelete}
        title="Delete this fade"
      >
        ×
      </button>
    </div>
  );
}

//
// Inline trash-can icon for the "clear all settings" button.
// Outlined rather than filled so it reads as a small, non-shouting
// glyph even at 14 px; stroke uses currentColor so it inherits the
// button's foreground.
//
function TrashIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

//
// One button in the panel's tab bar. Renders the label plus an
// optional numeric badge when `badge` is defined — used to flag
// non-default state on tabs that are currently hidden.
//
function TabButton(props: {
  active: boolean;
  onClick: () => void;
  badge?: number | undefined;
  children: ComponentChildren;
}): JSX.Element {
  return (
    <button style={tabButtonStyle(props.active)} onClick={props.onClick}>
      <span>{props.children}</span>
      {props.badge !== undefined && <span style={tabBadgeStyle}>{props.badge}</span>}
    </button>
  );
}

//
// Small numeric input with sensible defaults. Calls onChange with
// a parsed number only when the value is finite — keeps the parent
// from seeing NaN while the user is mid-edit.
//
function NumberInput(props: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}): JSX.Element {
  return (
    <input
      type="number"
      min={props.min}
      max={props.max}
      step={props.step ?? 1}
      value={props.value}
      disabled={props.disabled}
      onInput={(e) => {
        const raw = (e.currentTarget as HTMLInputElement).value;
        const n = Number(raw);
        if (Number.isFinite(n)) props.onChange(n);
      }}
      style={smallInputStyle}
    />
  );
}
